import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Legend,
  BarChart as RechartsBarChart,
  XAxis,
  YAxis,
} from 'recharts';
import { DEFAULT_ANIMATION, DEFAULT_AXIS_CONFIG } from '../constants';
import { BarChartProps } from '../types';
import {
  formatValue,
  getChartColors,
  isMobileDevice,
  reduceDataPoints,
} from '../utils';
import { ChartEmpty, ChartError, ChartLoading } from './ChartStates';

export function VerticalBarChart({
  data,
  dataKeys,
  format = 'number',
  currencyCode,
  stacked = false,
  colors,
  tooltip,
  xAxis,
  yAxis,
  legend,
  showLegend = false,
  animate = DEFAULT_ANIMATION.enabled,
  loading,
  error,
  emptyState,
  fillParent = false,
  className = '',
}: Omit<BarChartProps, 'layout' | 'showLabels'>) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(isMobileDevice());
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle loading state
  if (loading) {
    return <ChartLoading fillParent={fillParent} className={className} />;
  }

  // Handle error state
  if (error) {
    return (
      <ChartError error={error} fillParent={fillParent} className={className} />
    );
  }

  // Handle empty state
  if (!data || data.length === 0) {
    return (
      <ChartEmpty fillParent={fillParent} className={className}>
        {emptyState}
      </ChartEmpty>
    );
  }

  // Reduce data points on mobile
  const chartData = isMobile ? reduceDataPoints(data) : data;

  // Determine which keys to display
  const keys = dataKeys || ['value'];

  // Get colors for bars
  const barColors = colors || getChartColors(keys.length);

  // Build chart config
  const chartConfig: ChartConfig = keys.reduce((config, key, index) => {
    config[key] = {
      label: key.charAt(0).toUpperCase() + key.slice(1),
      color: barColors[index],
    };
    return config;
  }, {} as ChartConfig);

  // Custom formatter for tooltip
  const tooltipFormatter =
    tooltip?.formatter ||
    ((value: number) => {
      return [formatValue(value, format, currencyCode), ''];
    });

  // Axis tick formatter
  const xAxisFormatter =
    xAxis?.tickFormatter ||
    ((value: any) => {
      if (typeof value === 'string' && value.length > 15 && isMobile) {
        return value.substring(0, 12) + '...';
      }
      return value;
    });

  const yAxisFormatter =
    yAxis?.tickFormatter ||
    ((value: number) => {
      return formatValue(value, format, currencyCode);
    });

  // Map legend align to recharts format
  const legendAlign =
    legend?.align === 'start'
      ? 'left'
      : legend?.align === 'end'
        ? 'right'
        : 'center';

  const containerClass = fillParent
    ? `absolute inset-0 ${className}`
    : `w-full min-h-64 ${className}`;

  return (
    <div className={containerClass}>
      <ChartContainer config={chartConfig} className="h-full w-full">
        <RechartsBarChart
          data={chartData}
          margin={{ top: 0, right: 0, bottom: 0, left: -16 }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="label"
            tickLine={
              xAxis?.show !== false ? DEFAULT_AXIS_CONFIG.tickLine : false
            }
            tickMargin={DEFAULT_AXIS_CONFIG.tickMargin}
            axisLine={
              xAxis?.show !== false ? DEFAULT_AXIS_CONFIG.axisLine : false
            }
            tickFormatter={xAxisFormatter}
            hide={xAxis?.show === false}
            label={
              xAxis?.label
                ? {
                    value: xAxis.label,
                    position: 'insideBottom',
                    offset: -10,
                  }
                : undefined
            }
          />
          <YAxis
            tickLine={
              yAxis?.show !== false ? DEFAULT_AXIS_CONFIG.tickLine : false
            }
            axisLine={
              yAxis?.show !== false ? DEFAULT_AXIS_CONFIG.axisLine : false
            }
            tickMargin={DEFAULT_AXIS_CONFIG.tickMargin}
            tickFormatter={yAxisFormatter}
            hide={yAxis?.show === false}
            domain={yAxis?.domain}
            label={
              yAxis?.label
                ? {
                    value: yAxis.label,
                    angle: -90,
                    position: 'insideLeft',
                  }
                : undefined
            }
          />
          {tooltip?.enabled !== false && (
            <ChartTooltip
              cursor={false}
              content={
                typeof tooltip?.content === 'function' ? (
                  tooltip.content
                ) : tooltip?.content ? (
                  () => tooltip.content as any
                ) : (
                  <ChartTooltipContent formatter={tooltipFormatter as any} />
                )
              }
            />
          )}
          {showLegend && legend?.position && (
            <Legend
              verticalAlign={legend.verticalAlign || 'bottom'}
              align={legendAlign as any}
            />
          )}
          {keys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={barColors[index]}
              stackId={stacked ? 'stack' : undefined}
              radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
              isAnimationActive={animate}
              animationDuration={animate ? DEFAULT_ANIMATION.duration : 0}
            />
          ))}
        </RechartsBarChart>
      </ChartContainer>
    </div>
  );
}
